import { Inject, Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { take as takeOp } from 'rxjs/internal/operators/take';
import { concatMap, map } from 'rxjs/operators';
import { EnvironmentSettings } from 'src/app/models/environment-settings.model';
import { DocumentVersionInput, ProductDataPayload } from 'graphql/models/generated';
import { AppLooseArticle as LooseArticle } from 'graphql/models/extended';
import { LooseArticlesQuery } from 'graphql/queries/loose-articles';
import { OfferBase } from 'models/offer.model'
import { LooseArticlesStorage } from 'models/loose-article.model'
import { StoreService } from 'shared/services/store.service';

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private settingsConfig!: EnvironmentSettings;

  constructor(
    @Inject('envSettings') private envSettings: EnvironmentSettings,
    private storeService: StoreService,
    private looseArticlesQuery: LooseArticlesQuery
  ) {
    this.settingsConfig = this.envSettings;
  }

  getProductsList(
    perPage = 10,
    page = 0,
    categoryId: string,
    searchTerm?: string
  ): Observable<LooseArticle[]> {

    if (page === 0) {
      this.storeService.resetLooseArticles();
    }

    return combineLatest([
      this.storeService.getCurrentOffer(),
      this.storeService.getLooseArticles(),
    ])
    .pipe(
      concatMap(([offer, looseData]: [OfferBase, LooseArticlesStorage]): Observable<LooseArticle[]> => {
        let products = looseData?.items || [];
        const hasMore = looseData.hasMore;
        const currentCount = (perPage * page) + perPage;
        let currentPage = looseData.currentPage || 0;
        let sendRequest = true;

        if (products.length && products.length >= currentCount) {
          sendRequest = false;
        }

        if (hasMore && products.length && products.length < currentCount) {
          currentPage++;
        }

        if (!sendRequest) {
          return of(products);
        } else {
          return this.fetchProductsByCategory(
            offer,
            currentPage,
            categoryId,
            searchTerm
          ).pipe(
            map((productData) => {
              const filterItems = this.filterProductsByShowProp(productData.items) as LooseArticle[];
              const items = products.concat(filterItems) as LooseArticle[];

              this.storeService.setLooseArticles({
                items,
                hasMore: productData.hasMore,
                currentPage: productData.pageInfo.currentPage
              });

              return items;
            })
          )
        }
      }),
      map((items) => {
        const startIndex = perPage * page;
        const endIndex = perPage + startIndex;

        return items.slice(startIndex, endIndex);
      }),
      map((items) => {
        return this.mapProductsWithProps(items);
      })
    );
  }

  fetchProductsByCategory(
    offer: OfferBase,
    page = 0,
    categoryId: string,
    searchTerm?: string
  ): Observable<ProductDataPayload> {
    const documentVersion: DocumentVersionInput = {
      documentId: offer.documentId,
      revision: offer.revision
    };

    const salesArea = offer?.salesArea?.id || '';

    const variables = {
      documentVersion,
      id: categoryId,
      searchTerm,
      salesArea,
      paging: { page: page, perPage: 100 }
    };

    return this.looseArticlesQuery.fetch(variables, { fetchPolicy: 'no-cache' })
    .pipe<ProductDataPayload, ProductDataPayload>(
      map((r) => r.data?.products?.category?.products as ProductDataPayload),
      takeOp(1)
    );
  }

  private mapProductsWithProps(items: LooseArticle[]): LooseArticle[] {
    return items.map((item) => {
        const eanProp = item.properties && item.properties.find((p) => (p.name === 'EAN'));
        const externalImage = this.getImageUrlByEAN((eanProp && eanProp.value) || '', 't');

        return {
          ...item,
          externalImage
        };
      });
  }

  private filterProductsByShowProp(items: LooseArticle[]): LooseArticle[] {
    return items.map((item) => {
      const showIn = item.properties && item.properties.find((p) => (p.name === 'showInLoseArticle'));

      return {
        ...item,
        show: showIn?.value !== '0'
      };
    }).filter((item) => item.show);
  }

  getImageUrlByEAN(ean?: string, size?: ('t' | 'm' | 'l')): string {
    return ean
      ? `${this.settingsConfig.imageApiUrl}/image-ean${(size ? `/${size}/` : '/')}${ean}`
      : 'assets/common/images/product-default.svg';
  }
}
